import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import PageWrapper from "./layout/PageWrapper";
import PageHeader from "./layout/PageHeader";
import StatsCard from "./layout/StatsCard";
import FormStatsDateFilter from "./FormStatsDateFilter";
import PeopleIcon from "@mui/icons-material/People";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TodayIcon from "@mui/icons-material/Today";
import BarChartIcon from "@mui/icons-material/BarChart";

const AdminCustomersStats = ({
  stats,
  periodType,
  startDate,
  endDate,
  onPeriodTypeChange,
  onStartDateChange,
  onEndDateChange,
  onApplyFilters,
  onRefreshData,
}) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getTrendText = (trend) => {
    if (trend > 0) return `+${trend.toFixed(1)}%`;
    if (trend < 0) return `${trend.toFixed(1)}%`;
    return "0%";
  };

  const getTrendColor = (trend) => {
    if (trend > 0) return "success";
    if (trend < 0) return "error";
    return "default";
  };

  return (
    <PageWrapper>
      <PageHeader
        title="Statistiques des Clients"
        subtitle="Analysez l'évolution des inscriptions clients"
      />

      {/* Filtres de période */}
      <FormStatsDateFilter
        periodType={periodType}
        startDate={startDate}
        endDate={endDate}
        onPeriodTypeChange={onPeriodTypeChange}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
        onApplyFilters={onApplyFilters}
        onRefresh={onRefreshData}
      />

      {/* Cartes de statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatsCard
            title="Total Clients"
            value={stats.totalCustomers}
            icon={PeopleIcon}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatsCard
            title="Tendance"
            value={getTrendText(stats.trend)}
            icon={TrendingUpIcon}
            color={getTrendColor(stats.trend)}
            trend={
              stats.trend > 0 ? "up" : stats.trend < 0 ? "down" : "neutral"
            }
            trendValue={getTrendText(stats.trend)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatsCard
            title="Moyenne / Jour"
            value={stats.averagePerDay.toFixed(1)}
            icon={TodayIcon}
            color="info"
          />
        </Grid>
      </Grid>

      {/* Graphiques et tableaux */}
      <Grid container spacing={3}>
        {/* Graphique par date */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <BarChartIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="h6" component="h2">
                  Inscriptions par Date
                </Typography>
              </Box>

              {stats.byDate.length > 0 ? (
                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Date</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Clients</strong>
                        </TableCell>
                        <TableCell align="right" sx={{ width: "300px" }}>
                          <strong>Graphique</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats.byDate.map((item, index) => {
                        const maxCount = Math.max(
                          ...stats.byDate.map((d) => d.count)
                        );
                        const barWidth = (item.count / maxCount) * 100;

                        return (
                          <TableRow
                            key={index}
                            sx={{
                              "&:hover": {
                                backgroundColor: "action.hover",
                              },
                            }}
                          >
                            <TableCell>{formatDate(item.date)}</TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "medium" }}
                              >
                                {item.count}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${barWidth}%`,
                                    height: 8,
                                    bgcolor: "primary.main",
                                    borderRadius: 1,
                                    minWidth: barWidth > 0 ? "4px" : "0px",
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{ color: "text.secondary", minWidth: 30 }}
                                >
                                  {item.count}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary" align="center">
                  Aucune donnée disponible pour cette période
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PageWrapper>
  );
};

export default AdminCustomersStats;
